<!--
  Copyright (c) 2025 NoorWays. All rights reserved.
  Licensed under the terms specified in the LICENSE.txt file.
-->

<template>
  <div class="policy">
    <div class="container">
      <header class="policy-header">
        <h1>{{ $t('privacy.title') }}</h1>
        <p class="subtitle">{{ $t('privacy.intro') }}</p>
        <p class="effective-date">{{ $t('privacy.effectiveDate') }} {{ formattedDate }}</p>
      </header>

      <section class="policy-section">
        <h2>1. {{ $t('privacy.sections.whoWeAre.title') }}</h2>
        <p>{{ $t('privacy.sections.whoWeAre.body') }}</p>
      </section>

      <section class="policy-section">
        <h2>2. {{ $t('privacy.sections.informationWeCollect.title') }}</h2>
        <ul class="bulleted">
          <li>{{ $t('privacy.sections.informationWeCollect.items.accountInfo') }}</li>
          <li>{{ $t('privacy.sections.informationWeCollect.items.usageData') }}</li>
          <li>{{ $t('privacy.sections.informationWeCollect.items.contentProvided') }}</li>
          <li>{{ $t('privacy.sections.informationWeCollect.items.subscriptionPayments') }}</li>
          <li>{{ $t('privacy.sections.informationWeCollect.items.cookies') }}</li>
        </ul>
      </section>

      <section class="policy-section">
        <h2>3. {{ $t('privacy.sections.howWeUse.title') }}</h2>
        <ul class="bulleted">
          <li>{{ $t('privacy.sections.howWeUse.items.provide') }}</li>
          <li>{{ $t('privacy.sections.howWeUse.items.authenticate') }}</li>
          <li>{{ $t('privacy.sections.howWeUse.items.payments') }}</li>
          <li>{{ $t('privacy.sections.howWeUse.items.fraud') }}</li>
          <li>{{ $t('privacy.sections.howWeUse.items.support') }}</li>
          <li>{{ $t('privacy.sections.howWeUse.items.legal') }}</li>
        </ul>
        <p class="note">{{ $t('privacy.sections.howWeUse.note') }}</p>
      </section>

      <section class="policy-section">
        <h2>4. {{ $t('privacy.sections.storageSecurity.title') }}</h2>
        <p>{{ $t('privacy.sections.storageSecurity.body') }}</p>
      </section>

      <section class="policy-section">
        <h2>5. {{ $t('privacy.sections.thirdParties.title') }}</h2>
        <ul class="bulleted">
          <li>{{ $t('privacy.sections.thirdParties.items.payments') }}</li>
          <li>{{ $t('privacy.sections.thirdParties.items.hosting') }}</li>
          <li>{{ $t('privacy.sections.thirdParties.items.legal') }}</li>
        </ul>
        <p>{{ $t('privacy.sections.thirdParties.noSell') }}</p>
      </section>

      <section class="policy-section">
        <h2>6. {{ $t('privacy.sections.cookies.title') }}</h2>
        <p>{{ $t('privacy.sections.cookies.body') }}</p>
      </section>

      <section class="policy-section">
        <h2>7. {{ $t('privacy.sections.rights.title') }}</h2>
        <ul class="bulleted">
          <li>{{ $t('privacy.sections.rights.items.access') }}</li>
          <li>{{ $t('privacy.sections.rights.items.rectify') }}</li>
          <li>{{ $t('privacy.sections.rights.items.delete') }}</li>
          <li>{{ $t('privacy.sections.rights.items.restrict') }}</li>
          <li>{{ $t('privacy.sections.rights.items.portability') }}</li>
          <li>{{ $t('privacy.sections.rights.items.consent') }}</li>
        </ul>
        <p>
          {{ $t('privacy.sections.rights.contactPrefix') }}
          <a class="link" href="mailto:contact@noorways.fr">contact@noorways.fr</a>.
          {{ $t('privacy.sections.rights.ccpaNote') }}
        </p>
      </section>

      <section class="policy-section">
        <h2>8. {{ $t('privacy.sections.intlTransfers.title') }}</h2>
        <p>{{ $t('privacy.sections.intlTransfers.body') }}</p>
      </section>

      <section class="policy-section">
        <h2>9. {{ $t('privacy.sections.retention.title') }}</h2>
        <p>{{ $t('privacy.sections.retention.body') }}</p>
      </section>

      <section class="policy-section">
        <h2>10. {{ $t('privacy.sections.children.title') }}</h2>
        <p>{{ $t('privacy.sections.children.body') }}</p>
      </section>

      <section class="policy-section">
        <h2>11. {{ $t('privacy.sections.changes.title') }}</h2>
        <p>{{ $t('privacy.sections.changes.body') }}</p>
      </section>

      <section class="policy-section">
        <h2>12. {{ $t('privacy.sections.contact.title') }}</h2>
        <p>
          {{ $t('privacy.sections.contact.bodyPrefix') }}
          <a class="link" href="mailto:contact@noorways.fr">contact@noorways.fr</a>.
        </p>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useHead } from '@vueuse/head'
import { computed } from 'vue'

import { useI18n } from 'vue-i18n'

const { t, locale } = useI18n()
const effectiveDate = new Date('2025-09-15')
const formattedDate = computed(() =>
  new Intl.DateTimeFormat(locale.value, { dateStyle: 'long' }).format(effectiveDate)
)

useHead(() => ({
  title: t('privacy.title'),
  meta: [{ name: 'description', content: t('privacy.meta.description') }],
}))
</script>

<style lang="scss" scoped>
.policy {
  min-height: 100vh;
  background: var(--nw-bg-secondary);
  padding: 2rem 0;
}

.container {
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 1rem;
}

.policy-header {
  text-align: center;
  margin-bottom: 2rem;

  h1 {
    font-size: 2.25rem;
    color: var(--nw-text-primary);
    margin-bottom: 0.5rem;
  }

  .subtitle {
    color: var(--nw-text-secondary);
    max-width: 800px;
    margin: 0 auto 0.5rem auto;
  }

  .effective-date {
    color: var(--nw-text-secondary);
    font-size: 0.9rem;
  }
}

.policy-section {
  background: var(--nw-bg-primary);
  border: 1px solid var(--nw-border-light);
  border-radius: var(--nw-radius-lg);
  box-shadow: var(--nw-shadow-sm);
  padding: 1.25rem 1.5rem;
  margin-bottom: 1rem;

  h2 {
    color: var(--nw-primary);
    margin-bottom: 0.75rem;
    font-size: 1.25rem;
  }

  p {
    color: var(--nw-text-primary);
    line-height: 1.7;
  }

  .note {
    background: var(--nw-primary-lightest);
    border-left: 3px solid var(--nw-primary);
    padding: 0.75rem 1rem;
    border-radius: var(--nw-radius-sm);
    color: var(--nw-text-primary);
    margin-top: 0.5rem;
  }
}

.bulleted {
  margin: 0.5rem 0 0.5rem 1.2rem;
  color: var(--nw-text-primary);

  li {
    margin: 0.25rem 0;
    line-height: 1.6;
  }
}

.link {
  color: var(--nw-primary);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

/* Responsive */
@media (max-width: 768px) {
  .policy {
    padding: 1rem 0;
  }

  .policy-header h1 {
    font-size: 1.75rem;
  }

  .policy-section {
    padding: 1rem 1rem;
  }
}
</style>
